import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { 
  Crown, CreditCard, Check, ArrowRight, Zap, Loader2, 
  TrendingUp, ArrowDown, Info, AlertTriangle, Sparkles, X, Tag, ExternalLink
} from "lucide-react";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { useTheme } from "../../contexts/ThemeContext";
import { useIsMobileApp } from "../../hooks/useIsMobileApp";
import { getActiveDiscount, applyDiscount, computeAnnualPricing, annualRenewalNote } from "../../utils/planDiscounts";
import UsageTopupsSection from "../usage/UsageTopupsSection";

/**
 * MyPlanTab Component
 * Displays current plan, available plans, and subscription management options
 */
const MyPlanTab = ({
  user,
  setUser,
  limits,
  availablePlans,
  availableDiscounts = [],
  paymentHistory,
  billingPeriod,
  setBillingPeriod,
  refetchLimits,
  API,
  getAuthConfig,
}) => {
  const isMobileApp = useIsMobileApp();
  const { isDark } = useTheme();
  const [upgradingPlan, setUpgradingPlan] = useState(null);
  const [changePlanModalOpen, setChangePlanModalOpen] = useState(false);
  const [selectedPlanChange, setSelectedPlanChange] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoValidation, setPromoValidation] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // A lapsed/cancelled/incomplete subscription means every plan selection must
  // go through Stripe Checkout (payment + setup) — there is nothing to "downgrade".
  const hasActiveSubscription = user?.subscription_status === "active";

  // Auto-open the upgrade flow if the user selected a plan on the landing/pricing page
  // pre-registration. `pending_plan_selection` is set by RegisterPage after signup.
  // IMPORTANT: hooks must run unconditionally — keep this above the `if (isMobileApp)` return.
  useEffect(() => {
    if (!availablePlans || availablePlans.length === 0) return;
    const raw = localStorage.getItem("pending_plan_selection");
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      // Expire after 10 minutes so we don't re-open on every future visit
      if (!parsed.plan_id || (Date.now() - (parsed.at || 0)) > 10 * 60 * 1000) {
        localStorage.removeItem("pending_plan_selection");
        return;
      }
      const plan = availablePlans.find(p => p.plan_id === parsed.plan_id);
      if (plan) {
        // Skip the auto-upgrade if the plan can't be checked out (no
        // price OR no Stripe price ID). Otherwise we'd push the user
        // straight to /payments/checkout which 400s with "Invalid plan
        // price" — bricking the post-signup flow. The user can still
        // browse all plans and contact sales from this page.
        const hasNoCheckoutTarget =
          (typeof plan.price !== "number" || plan.price <= 0) ||
          (!plan.stripe_price_id && !plan.stripe_price_id_annual);
        if (hasNoCheckoutTarget) {
          localStorage.removeItem("pending_plan_selection");
          return;
        }
        const currentPlan = availablePlans.find(p =>
          p.plan_id?.toLowerCase() === user?.subscription_plan?.toLowerCase() ||
          p.name?.toLowerCase() === user?.subscription_plan?.toLowerCase()
        );
        const changeType = !hasActiveSubscription || !currentPlan || plan.price > currentPlan.price ? "upgrade" : "downgrade";
        setSelectedPlanChange({ ...plan, changeType });
        setChangePlanModalOpen(true);
      }
      localStorage.removeItem("pending_plan_selection");
    } catch {
      localStorage.removeItem("pending_plan_selection");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePlans]);

  // Sync/reconcile usage data
  const syncUsage = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/usage/reconcile`, {}, getAuthConfig());
      const data = res.data;
      toast.success(`Usage synced: ${data.after.sms_used} SMS units, ${data.after.minutes_used} minutes this billing period`);
      if (refetchLimits) refetchLimits();
    } catch (error) {
      toast.error("Failed to sync usage data");
    } finally {
      setSyncing(false);
    }
  };

  // Open Stripe Customer Portal
  const openCustomerPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await axios.post(`${API}/stripe/customer-portal`, {
        return_url: window.location.href,
      }, getAuthConfig());
      if (res.data.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open customer portal. You may need an active Stripe subscription first.");
      setOpeningPortal(false);
    }
  };

  // In mobile app, show web-only message instead of payment flows.
  // On iOS, no reference to external purchase locations (Apple 3.1.1).
  if (isMobileApp) {
    const isIOSShell = window.FieldzNative?.platform === "ios";
    return (
      <Card className="glass-card border-theme">
        <CardContent className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#FFCC00]/10 flex items-center justify-center mb-4">
            <Crown className="w-8 h-8 text-[#FFCC00]" />
          </div>
          <h3 className="text-lg font-semibold text-foreground font-['Manrope'] mb-2">
            Current Plan: <span className="capitalize text-[#FFCC00]">{user?.subscription_plan || "Starter"}</span>
          </h3>
          <p className="text-muted-foreground text-sm max-w-sm mb-6 leading-relaxed">
            {isIOSShell
              ? "Subscription management, plan upgrades, and billing are not available in this app."
              : "Subscription management, plan upgrades, and billing are available on the web version."}
          </p>
          {!isIOSShell && (
            <div className="bg-muted/30 rounded-xl p-4 border border-theme w-full max-w-sm">
              <p className="text-foreground text-sm font-medium mb-1">Manage your subscription at:</p>
              <p className="text-[#FFCC00] text-sm font-semibold" data-testid="web-subscription-url">fieldzai.com</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Get current plan data
  const getCurrentPlan = () => {
    return availablePlans.find(p => 
      p.plan_id?.toLowerCase() === user?.subscription_plan?.toLowerCase() ||
      p.name?.toLowerCase() === user?.subscription_plan?.toLowerCase()
    );
  };

  // Determine if a plan is an upgrade or downgrade
  const getPlanChangeType = (targetPlan) => {
    if (!hasActiveSubscription) return "upgrade";
    const currentPlan = getCurrentPlan();
    if (!currentPlan || !targetPlan) return "upgrade";
    return targetPlan.price > currentPlan.price ? "upgrade" : "downgrade";
  };

  // Calculate price based on billing period.
  // The admin-configured discount (e.g. "20% off for first 12 months") is
  // applied to the monthly base price BEFORE the annual 20% multiplier,
  // so the displayed numbers stack correctly with the public landing page.
  // Monthly pricing: admin discount applied to the monthly price.
  // Annual pricing: discount applied to the ANNUAL INVOICE amount (mirrors
  // how the backend attaches the Stripe coupon at checkout).
  const getDiscountedMonthly = (plan) => {
    const discount = getActiveDiscount(plan, availableDiscounts, "monthly");
    return discount ? applyDiscount(plan.price, discount) : plan.price;
  };

  const getAnnualInfo = (plan) => computeAnnualPricing(plan, availableDiscounts);

  const getDisplayPrice = (plan) => {
    if (billingPeriod === "annual") {
      return Math.round(getAnnualInfo(plan).finalAnnual / 12);
    }
    return getDiscountedMonthly(plan);
  };

  const getAnnualTotal = (plan) => getAnnualInfo(plan).finalAnnual;

  // Validate promo code
  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setValidatingPromo(true);
    setPromoValidation(null);
    try {
      const res = await axios.post(`${API}/payments/validate-promo`, {
        code: promoCode,
        plan_id: selectedPlanChange?.plan_id,
        billing_period: billingPeriod,
      }, getAuthConfig());
      setPromoValidation({ valid: true, ...res.data });
      toast.success("Promo code applied!");
    } catch (err) {
      setPromoValidation({ valid: false, message: err.response?.data?.detail || "Invalid code" });
      toast.error(err.response?.data?.detail || "Invalid promo code");
    } finally { setValidatingPromo(false); }
  };

  // Handle plan upgrade
  const handleUpgrade = async (plan) => {
    if (upgradingPlan) return;
    
    setUpgradingPlan(plan.plan_id);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        plan_id: plan.plan_id,
        origin_url: window.location.origin,
        billing_period: billingPeriod,
        promo_code: promoValidation?.valid ? promoCode : undefined,
      }, getAuthConfig());
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Could not create checkout session. Please try again.");
        setUpgradingPlan(null);
      }
    } catch (error) {
      console.error("Upgrade error:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout. Please try again.");
      setUpgradingPlan(null);
    }
  };

  // Handle plan change (upgrade, downgrade, or reactivation of current plan)
  const handlePlanChange = async (plan) => {
    const isCurrentPlan =
      user?.subscription_plan?.toLowerCase() === plan.plan_id?.toLowerCase() ||
      user?.subscription_plan?.toLowerCase() === plan.name?.toLowerCase();
    const changeType = isCurrentPlan && !hasActiveSubscription ? "reactivate" : getPlanChangeType(plan);
    setSelectedPlanChange({ ...plan, changeType });
    setChangePlanModalOpen(true);
  };

  // Confirm and process plan change
  const confirmPlanChange = async () => {
    if (!selectedPlanChange || upgradingPlan) return;
    
    if (selectedPlanChange.changeType !== "downgrade") {
      // Upgrades AND reactivations go through Stripe Checkout (payment + setup)
      await handleUpgrade(selectedPlanChange);
      setChangePlanModalOpen(false);
      setSelectedPlanChange(null);
    } else {
      setUpgradingPlan(selectedPlanChange.plan_id);
      try {
        const res = await axios.post(`${API}/stripe/change-plan`, {
          new_plan_id: selectedPlanChange.plan_id,
          change_type: "downgrade"
        }, getAuthConfig());
        
        toast.success(res.data.message || "Plan change scheduled successfully!");
        setChangePlanModalOpen(false);
        setSelectedPlanChange(null);
        
        const userRes = await axios.get(`${API}/auth/me`, getAuthConfig());
        if (userRes.data) {
          setUser(userRes.data);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to change plan. Please try again.");
      } finally {
        setUpgradingPlan(null);
      }
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Current Plan Card */}
      <Card className="glass-card border-theme overflow-hidden" data-testid="current-plan-card">
        <div className="bg-gradient-to-r from-[#0046BE]/30 to-[#FFCC00]/20 p-4 sm:p-6 border-b border-theme">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#FFCC00]/20 flex items-center justify-center flex-shrink-0">
                <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-[#FFCC00]" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-foreground font-['Manrope'] capitalize">
                  {user?.subscription_plan || "Free"} Plan
                </h3>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {user?.subscription_status === "active" ? "Active subscription" : "Subscription paused"}
                </p>
              </div>
            </div>
            <Badge className={`self-start sm:self-auto ${user?.subscription_status === "active" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
              {user?.subscription_status || "Active"}
            </Badge>
          </div>
        </div>
        <CardContent className="p-4 sm:pt-6">
          {/* Usage Summary */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-muted-foreground text-xs">Monthly Usage</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={syncUsage}
              disabled={syncing}
              data-testid="sync-usage-btn"
              className="text-xs h-7 px-2 text-muted-foreground hover:text-foreground"
            >
              {syncing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <TrendingUp className="w-3 h-3 mr-1" />}
              {syncing ? "Syncing..." : "Sync Usage"}
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-theme-surface rounded-xl p-3 sm:p-4 border border-theme">
              <div className="text-muted-foreground text-xs sm:text-sm mb-1">Team Members</div>
              <div className="text-lg sm:text-2xl font-bold text-foreground">
                {limits?.limits?.team_members?.used || 0}
                <span className="text-muted-foreground text-sm sm:text-base font-normal">
                  /{limits?.limits?.team_members?.unlimited ? "∞" : limits?.limits?.team_members?.limit || 2}
                </span>
              </div>
            </div>
            <div className="bg-theme-surface rounded-xl p-3 sm:p-4 border border-theme">
              <div className="text-muted-foreground text-xs sm:text-sm mb-1">Phone Lines</div>
              <div className="text-lg sm:text-2xl font-bold text-foreground">
                {limits?.limits?.dedicated_lines?.used || 0}
                <span className="text-muted-foreground text-sm sm:text-base font-normal">
                  /{limits?.limits?.dedicated_lines?.unlimited ? "∞" : limits?.limits?.dedicated_lines?.limit || 2}
                </span>
              </div>
            </div>
            <div className="bg-theme-surface rounded-xl p-3 sm:p-4 border border-theme">
              <div className="text-muted-foreground text-xs sm:text-sm mb-1">SMS Used</div>
              <div className="text-lg sm:text-2xl font-bold text-foreground">
                {limits?.limits?.sms?.used || 0}
                <span className="text-muted-foreground text-sm sm:text-base font-normal">
                  /{limits?.limits?.sms?.unlimited ? "∞" : (limits?.limits?.sms?.limit || 500).toLocaleString()}
                </span>
              </div>
            </div>
            <div className="bg-theme-surface rounded-xl p-3 sm:p-4 border border-theme">
              <div className="text-muted-foreground text-xs sm:text-sm mb-1">Minutes Used</div>
              <div className="text-lg sm:text-2xl font-bold text-foreground">
                {limits?.limits?.minutes?.used || 0}
                <span className="text-muted-foreground text-sm sm:text-base font-normal">
                  /{limits?.limits?.minutes?.unlimited ? "∞" : (limits?.limits?.minutes?.limit || 500).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Current Plan Features */}
          <div className="border-t border-theme pt-4 sm:pt-6">
            <h4 className="text-foreground font-medium mb-3 sm:mb-4 text-sm sm:text-base">Your Plan Includes</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
              {[
                { label: "Team Members", value: limits?.limits?.team_members?.unlimited ? "Unlimited" : `Up to ${limits?.limits?.team_members?.limit || 2}`, included: true },
                { label: "Phone Lines", value: limits?.limits?.dedicated_lines?.unlimited ? "Unlimited" : `Up to ${limits?.limits?.dedicated_lines?.limit || 2}`, included: true },
                { label: "SMS Messages", value: limits?.limits?.sms?.unlimited ? "Unlimited" : `${(limits?.limits?.sms?.limit || 500).toLocaleString()}/mo`, included: true },
                { label: "Voice Minutes", value: limits?.limits?.minutes?.unlimited ? "Unlimited" : `${(limits?.limits?.minutes?.limit || 500).toLocaleString()}/mo`, included: true },
                { label: "Call Masking", value: limits?.features?.call_masking ? "Included" : "Not included", included: limits?.features?.call_masking },
                { label: "Fieldz AI", value: limits?.features?.joby_automation ? "Included" : "Not included", included: limits?.features?.joby_automation },
                { label: "QuickBooks", value: limits?.features?.quickbooks_integration ? "Included" : "Not included", included: limits?.features?.quickbooks_integration },
                { label: "Customer Portal", value: limits?.features?.customer_portal ? "Included" : "Not included", included: limits?.features?.customer_portal },
                { label: "24/7 Chat Support", value: limits?.features?.chat_support_24_7 ? "Included" : "Not included", included: limits?.features?.chat_support_24_7 },
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-muted-foreground">
                  {feature.included !== false ? (
                    <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border border-white/20 flex-shrink-0" />
                  )}
                  <span className="text-xs sm:text-sm">{feature.label}: <span className="text-foreground">{feature.value}</span></span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage & Top-Ups */}
      <UsageTopupsSection API={API} getAuthConfig={getAuthConfig} refetchLimits={refetchLimits} />

      {/* Available Plans */}
      <Card className="glass-card border-theme" data-testid="available-plans-card">
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div>
              <CardTitle className="text-foreground font-['Manrope'] text-base sm:text-lg">Available Plans</CardTitle>
              <CardDescription className="text-muted-foreground text-xs sm:text-sm">
                Upgrade to unlock more features and capacity
              </CardDescription>
            </div>
            {!hasActiveSubscription && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs sm:text-sm" data-testid="inactive-subscription-banner">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Your subscription is inactive. Select a plan below to set up payment and reactivate your account.</span>
              </div>
            )}
            {/* Billing Period Toggle */}
            <div className="flex items-center gap-1 bg-theme-surface rounded-lg p-1 w-fit" data-testid="billing-toggle">
              <button
                onClick={() => { setBillingPeriod("monthly"); setPromoValidation(null); }}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all ${
                  billingPeriod === "monthly"
                    ? "bg-[#0046BE] text-white"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="billing-monthly-btn"
              >
                Monthly
              </button>
              <button
                onClick={() => { setBillingPeriod("annual"); setPromoValidation(null); }}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-all flex items-center gap-1 sm:gap-2 ${
                  billingPeriod === "annual"
                    ? "bg-[#0046BE] text-white"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="billing-annual-btn"
              >
                Annual
                <Badge className="bg-green-500/20 text-green-400 text-[10px] sm:text-xs">-20%</Badge>
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {availablePlans.filter(p => p.is_active).map((plan) => {
              const isCurrentPlan = user?.subscription_plan?.toLowerCase() === plan.plan_id?.toLowerCase() || 
                                    user?.subscription_plan?.toLowerCase() === plan.name?.toLowerCase();
              const displayPrice = getDisplayPrice(plan);
              const annualTotal = getAnnualTotal(plan);
              const annualInfo = billingPeriod === "annual" ? getAnnualInfo(plan) : null;
              const planDiscount = billingPeriod === "annual"
                ? annualInfo.discount
                : getActiveDiscount(plan, availableDiscounts, "monthly");
              const hasDiscount = billingPeriod === "annual"
                ? annualInfo.hasDiscount
                : (!!planDiscount && getDiscountedMonthly(plan) < plan.price);
              const currentPlanData = getCurrentPlan();
              const isUpgrade = !hasActiveSubscription || (currentPlanData ? plan.price > currentPlanData.price : true);
              const isReactivate = isCurrentPlan && !hasActiveSubscription;
              return (
                <div
                  key={plan.plan_id}
                  className={`relative rounded-xl border p-5 transition-all ${
                    isCurrentPlan
                      ? "bg-[#0046BE]/20 border-[#0046BE]/50"
                      : plan.is_popular
                      ? "bg-[#FFCC00]/10 border-[#FFCC00]/30 hover:border-[#FFCC00]/50"
                      : "bg-theme-surface border-theme hover:border-white/20"
                  }`}
                  data-testid={`plan-card-${plan.plan_id}`}
                >
                  {plan.is_popular && (
                    <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#FFCC00] text-black text-xs">
                      Most Popular
                    </Badge>
                  )}
                  {isCurrentPlan && (
                    <Badge className="absolute -top-2 right-4 bg-[#0046BE] text-white text-xs">
                      Current Plan
                    </Badge>
                  )}
                  <div className="text-center mb-4 pt-2">
                    <h4 className="text-lg font-bold text-foreground">{plan.name}</h4>
                    {hasDiscount && planDiscount.badge_label && (
                      <Badge
                        className="mt-1 bg-red-500/15 text-red-400 border-red-500/30 text-[10px] uppercase tracking-wider"
                        data-testid={`plan-discount-badge-${plan.plan_id}`}
                      >
                        {planDiscount.badge_label}
                      </Badge>
                    )}
                    <div className="mt-2">
                      {hasDiscount && (
                        <span
                          className="text-base text-muted-foreground line-through mr-2"
                          data-testid={`plan-original-price-${plan.plan_id}`}
                        >
                          ${billingPeriod === "annual" ? Math.round((annualInfo?.annualBase || 0) / 12) : plan.price}
                        </span>
                      )}
                      <span
                        className={`text-3xl font-bold ${hasDiscount ? "text-[#FFCC00]" : "text-foreground"}`}
                        data-testid={`plan-final-price-${plan.plan_id}`}
                      >
                        ${displayPrice}
                      </span>
                      <span className="text-muted-foreground text-sm">/mo</span>
                    </div>
                    {hasDiscount && planDiscount.marketing_text && (
                      <p
                        className="text-[11px] font-semibold text-red-400 mt-1"
                        data-testid={`plan-discount-text-${plan.plan_id}`}
                      >
                        {planDiscount.marketing_text}
                      </p>
                    )}
                    {billingPeriod === "annual" && (
                      <div className="mt-1" data-testid={`plan-annual-breakdown-${plan.plan_id}`}>
                        {hasDiscount && (
                          <span className="text-muted-foreground text-sm line-through mr-1" data-testid={`plan-annual-original-${plan.plan_id}`}>
                            ${annualInfo.annualBase}/yr
                          </span>
                        )}
                        <p className="text-green-400 text-xs mt-1">${annualTotal}/year billed annually</p>
                        {annualInfo.annualBase > 0 && (
                          <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`plan-renewal-note-${plan.plan_id}`}>
                            {annualRenewalNote(planDiscount, annualInfo.annualBase, annualInfo.finalAnnual)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  <ul className="space-y-2 mb-4">
                    {(plan.features || []).slice(0, 5).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${
                      isCurrentPlan && hasActiveSubscription
                        ? "bg-green-500/20 text-green-400 border border-green-500/30 cursor-not-allowed"
                        : isUpgrade
                          ? "bg-[#0046BE] text-white hover:bg-[#0046BE]/80"
                          : "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30"
                    }`}
                    disabled={(isCurrentPlan && hasActiveSubscription) || upgradingPlan === plan.plan_id}
                    onClick={() => {
                      if (!isCurrentPlan || !hasActiveSubscription) {
                        handlePlanChange(plan);
                      }
                    }}
                    data-testid={`upgrade-btn-${plan.plan_id}`}
                  >
                    {isCurrentPlan && hasActiveSubscription ? (
                      <span className="text-green-400">Current Plan</span>
                    ) : upgradingPlan === plan.plan_id ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isReactivate ? (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Reactivate Plan
                      </>
                    ) : isUpgrade ? (
                      <>
                        Upgrade <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                        Downgrade
                      </>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
          {availablePlans.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No plans available. Contact support for pricing information.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Change Confirmation Modal */}
      <Dialog open={changePlanModalOpen} onOpenChange={setChangePlanModalOpen}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-[#0a0a12] border-white/10' : 'bg-white border-gray-200'}`}>
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
              {selectedPlanChange?.changeType === "downgrade" ? (
                <>
                  <ArrowDown className="w-5 h-5 text-yellow-400" />
                  Downgrade to {selectedPlanChange?.name}
                </>
              ) : selectedPlanChange?.changeType === "reactivate" ? (
                <>
                  <Zap className="w-5 h-5 text-[#FFCC00]" />
                  Reactivate {selectedPlanChange?.name}
                </>
              ) : (
                <>
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  Upgrade to {selectedPlanChange?.name}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPlanChange && (
            <div className="space-y-4">
              {/* Plan Comparison */}
              <div className={`grid grid-cols-2 gap-4 p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                <div className="space-y-2">
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Current Plan</p>
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-[#FFCC00]" />
                    <span className="font-semibold text-foreground capitalize">{user?.subscription_plan}</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    ${getCurrentPlan()?.price || 0}<span className="text-sm text-muted-foreground font-normal">/mo</span>
                  </p>
                </div>
                
                <div className="space-y-2">
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">New Plan</p>
                  <div className="flex items-center gap-2">
                    <Sparkles className={`w-5 h-5 ${selectedPlanChange.changeType === "upgrade" ? "text-green-400" : "text-yellow-400"}`} />
                    <span className="font-semibold text-foreground">{selectedPlanChange.name}</span>
                  </div>
                  <p className="text-2xl font-bold text-foreground">
                    ${selectedPlanChange.price}<span className="text-sm text-muted-foreground font-normal">/mo</span>
                  </p>
                </div>
              </div>

              {/* Price Difference */}
              <div className={`p-4 rounded-xl border ${
                selectedPlanChange.changeType === "upgrade" 
                  ? "bg-green-500/10 border-green-500/20" 
                  : "bg-yellow-500/10 border-yellow-500/20"
              }`}>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Monthly Difference</span>
                  <span className={`font-semibold ${
                    selectedPlanChange.changeType === "upgrade" ? "text-green-400" : "text-yellow-400"
                  }`}>
                    {selectedPlanChange.changeType === "upgrade" ? "+" : "-"}
                    ${Math.abs(selectedPlanChange.price - (getCurrentPlan()?.price || 0))}/mo
                  </span>
                </div>
              </div>

              {/* Info Message */}
              {selectedPlanChange.changeType === "downgrade" ? (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    Your downgrade will take effect at the end of your current billing period. You&apos;ll continue to have access to your current plan features until then.
                  </p>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    {selectedPlanChange.changeType === "reactivate"
                      ? "Your subscription is currently inactive. You'll be redirected to Stripe to set up payment and reactivate your plan."
                      : "You'll be redirected to Stripe to complete the payment. Your new features will be available immediately after payment."}
                  </p>
                </div>
              )}

              {/* New Plan Features */}
              <div>
                <p className="text-muted-foreground text-sm mb-2">Features included in {selectedPlanChange.name}:</p>
                <div className="grid grid-cols-2 gap-2">
                  {(selectedPlanChange.features || []).slice(0, 6).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Annual checkout summary — original price, discount, due today, renewal terms */}
              {billingPeriod === "annual" && selectedPlanChange.changeType !== "downgrade" && (() => {
                const ai = computeAnnualPricing(selectedPlanChange, availableDiscounts);
                return (
                  <div className="p-4 rounded-xl border bg-blue-500/10 border-blue-500/20 space-y-1.5" data-testid="annual-checkout-summary">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Annual plan price</span>
                      <span className={ai.hasDiscount ? "line-through text-muted-foreground" : "font-semibold text-foreground"} data-testid="annual-original-price">
                        ${ai.annualBase}/yr
                      </span>
                    </div>
                    {ai.hasDiscount && (
                      <div className="flex items-center justify-between text-sm" data-testid="annual-discount-line">
                        <span className="text-muted-foreground">Discount{ai.discount?.name ? ` — ${ai.discount.name}` : ""}</span>
                        <span className="text-green-400 font-medium">−${ai.annualBase - ai.finalAnnual}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm font-semibold border-t border-blue-500/20 pt-1.5">
                      <span className="text-foreground">Due today</span>
                      <span className="text-foreground" data-testid="annual-due-today">${ai.finalAnnual}</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground" data-testid="annual-renewal-terms">
                      {annualRenewalNote(ai.discount, ai.annualBase, ai.finalAnnual)}
                    </p>
                  </div>
                );
              })()}

              {/* Promo Code */}
              {selectedPlanChange.changeType !== "downgrade" && (
                <div className="space-y-2">
                  <p className="text-muted-foreground text-sm">Have a promo code?</p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Enter promo code"
                        value={promoCode}
                        onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoValidation(null); }}
                        className="pl-9 input-glass font-mono uppercase"
                        data-testid="checkout-promo-input"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={validatePromo}
                      disabled={validatingPromo || !promoCode.trim()}
                      className="border-theme"
                      data-testid="apply-promo-btn"
                    >
                      {validatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </Button>
                  </div>
                  {promoValidation?.valid && (
                    <div className="flex items-center gap-2 text-green-400 text-sm bg-green-500/10 p-2 rounded-lg" data-testid="promo-applied-badge">
                      <Check className="w-4 h-4" />
                      <span>
                        {promoValidation.discount_type === "percentage"
                          ? `${promoValidation.discount_value}% off`
                          : `$${promoValidation.discount_value} off`
                        }
                        {promoValidation.duration === "once" && " (one-time)"}
                        {promoValidation.duration === "repeating" && ` for ${promoValidation.duration_months} month${promoValidation.duration_months > 1 ? "s" : ""}`}
                        {promoValidation.duration === "forever" && " (forever)"}
                      </span>
                    </div>
                  )}
                  {promoValidation?.valid && getActiveDiscount(selectedPlanChange, availableDiscounts, billingPeriod) && (
                    <p className="text-[11px] text-amber-400" data-testid="promo-no-stack-note">
                      Your promo code replaces the automatic discount — discounts don't stack.
                    </p>
                  )}
                  {promoValidation && !promoValidation.valid && (
                    <p className="text-red-400 text-sm">{promoValidation.message}</p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setChangePlanModalOpen(false);
                    setSelectedPlanChange(null);
                    setPromoCode("");
                    setPromoValidation(null);
                  }}
                  className="text-muted-foreground"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmPlanChange}
                  disabled={upgradingPlan}
                  className={selectedPlanChange.changeType !== "downgrade"
                    ? "btn-primary" 
                    : "bg-yellow-500 hover:bg-yellow-600 text-black"
                  }
                  data-testid="confirm-plan-change-btn"
                >
                  {upgradingPlan ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : selectedPlanChange.changeType !== "downgrade" ? (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Proceed to Payment
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Confirm Downgrade
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Subscription Management */}
      <Card className="glass-card border-theme" data-testid="subscription-management-card">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-foreground font-['Manrope'] text-base sm:text-lg">Subscription Management</CardTitle>
          <CardDescription className="text-muted-foreground text-xs sm:text-sm">
            Manage your subscription settings
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 space-y-4">
          {/* Current Subscription Status */}
          <div className={`flex items-center justify-between p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                user?.subscription_status === "active" ? "bg-green-500/20" : "bg-yellow-500/20"
              }`}>
                <Crown className={`w-5 h-5 ${
                  user?.subscription_status === "active" ? "text-green-400" : "text-yellow-400"
                }`} />
              </div>
              <div>
                <h4 className="font-medium text-foreground capitalize">{user?.subscription_plan || "Free"} Plan</h4>
                <p className="text-sm text-muted-foreground">
                  Status: <span className={user?.subscription_status === "active" ? "text-green-400" : "text-yellow-400"}>
                    {user?.subscription_status === "active" ? "Active" : "Inactive"}
                  </span>
                  {user?.pending_plan_change && (
                    <span className="text-yellow-400 ml-2">
                      (Downgrade to {user.pending_plan_change} pending)
                    </span>
                  )}
                </p>
              </div>
            </div>
            {user?.subscription_plan && user?.subscription_plan !== "free" && (
              <Badge className="bg-[#0046BE]/20 text-[#0046BE]">
                {billingPeriod === "annual" ? "Annual" : "Monthly"} Billing
              </Badge>
            )}
          </div>

          {/* Stripe Customer Portal */}
          {user?.subscription_plan && user?.subscription_plan !== "free" && user?.subscription_plan !== "starter" && (
            <div className={`p-4 rounded-xl border ${isDark ? 'bg-[#0046BE]/10 border-[#0046BE]/30' : 'bg-blue-50 border-blue-200'}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="font-medium text-foreground text-sm sm:text-base">Stripe Customer Portal</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Update payment method, view invoices, and manage your subscription directly on Stripe.
                  </p>
                </div>
                <Button
                  onClick={openCustomerPortal}
                  disabled={openingPortal}
                  className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80 flex-shrink-0"
                  data-testid="manage-subscription-btn"
                >
                  {openingPortal ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4 mr-2" />
                  )}
                  Manage Subscription
                </Button>
              </div>
            </div>
          )}

          {/* Quick Plan Change Buttons */}
          {user?.subscription_plan && user?.subscription_plan !== "free" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {availablePlans.filter(p => p.is_active).map((plan) => {
                const isCurrentPlan = plan.plan_id?.toLowerCase() === user?.subscription_plan?.toLowerCase() ||
                                     plan.name?.toLowerCase() === user?.subscription_plan?.toLowerCase();
                const currentPlan = getCurrentPlan();
                const isUpgrade = currentPlan ? plan.price > currentPlan.price : true;
                
                if (isCurrentPlan) return null;
                
                return (
                  <Button
                    key={plan.plan_id}
                    variant="outline"
                    onClick={() => handlePlanChange(plan)}
                    disabled={upgradingPlan === plan.plan_id}
                    className={`${isDark ? 'border-white/20 text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} ${
                      isUpgrade ? 'hover:border-green-500/50' : 'hover:border-yellow-500/50'
                    }`}
                    data-testid={`change-to-${plan.plan_id}-btn`}
                  >
                    {upgradingPlan === plan.plan_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : isUpgrade ? (
                      <TrendingUp className="w-4 h-4 mr-2 text-green-400" />
                    ) : (
                      <ArrowDown className="w-4 h-4 mr-2 text-yellow-400" />
                    )}
                    {isUpgrade ? "Upgrade to" : "Downgrade to"} {plan.name}
                  </Button>
                );
              })}
            </div>
          )}

          {/* Cancel Subscription */}
          {user?.subscription_plan && user?.subscription_plan !== "free" && (
            <div className={`pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid="cancel-subscription-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel Subscription
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className={isDark ? 'bg-[#0a0a12] border-white/10' : 'bg-white border-gray-200'}>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-foreground">Cancel Subscription?</AlertDialogTitle>
                    <AlertDialogDescription className="text-muted-foreground">
                      Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className={isDark ? 'bg-white/5 text-white border-white/10' : ''}>Keep Subscription</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/stripe/cancel-subscription`, {}, getAuthConfig());
                          toast.success("Subscription cancelled. You will retain access until the end of your billing period.");
                          const userRes = await axios.get(`${API}/auth/me`, getAuthConfig());
                          if (userRes.data) setUser(userRes.data);
                        } catch (error) {
                          toast.error(error.response?.data?.detail || "Failed to cancel subscription");
                        }
                      }}
                      className="bg-red-500 hover:bg-red-600"
                      data-testid="confirm-cancel-subscription-btn"
                    >
                      Yes, Cancel Subscription
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          {/* Help Text */}
          <p className="text-xs text-muted-foreground text-center pt-2">
            Need help? Contact us at <a href="mailto:support@fieldzai.com" className="text-[#FFCC00] hover:underline">support@fieldzai.com</a>
          </p>
        </CardContent>
      </Card>

      {/* Billing Info */}
      <Card className="glass-card border-theme" data-testid="billing-info-card">
        <CardHeader>
          <CardTitle className="text-foreground font-['Manrope']">Billing & Payment History</CardTitle>
          <CardDescription className="text-muted-foreground">
            View your payment history and transaction details
          </CardDescription>
        </CardHeader>
        <CardContent>
          {paymentHistory.length > 0 ? (
            <div className="space-y-3">
              {paymentHistory.map((txn, idx) => (
                <div key={txn.transaction_id || idx} className="flex items-center justify-between p-4 rounded-xl bg-theme-surface border border-theme" data-testid={`payment-txn-${idx}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      txn.payment_status === "paid" ? "bg-green-500/20" : 
                      txn.payment_status === "pending" ? "bg-yellow-500/20" : "bg-red-500/20"
                    }`}>
                      <CreditCard className={`w-5 h-5 ${
                        txn.payment_status === "paid" ? "text-green-400" : 
                        txn.payment_status === "pending" ? "text-yellow-400" : "text-red-400"
                      }`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{txn.plan_name || "Plan Upgrade"}</h4>
                      <p className="text-sm text-muted-foreground">
                        {new Date(txn.created_at).toLocaleDateString("en-US", { 
                          year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-foreground">${txn.amount?.toFixed(2) || "0.00"}</p>
                    <Badge className={`text-xs ${
                      txn.payment_status === "paid" ? "bg-green-500/20 text-green-400" : 
                      txn.payment_status === "pending" ? "bg-yellow-500/20 text-yellow-400" : "bg-red-500/20 text-red-400"
                    }`}>
                      {txn.payment_status || "Unknown"}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-theme-surface rounded-xl p-6 border border-theme text-center">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
              <p className="text-muted-foreground mb-4">
                No payment history yet. Upgrade your plan to see transactions here.
              </p>
              <Button variant="outline" className="border-theme text-foreground hover:bg-theme-hover" onClick={() => toast.info("Contact support@fieldzai.com for billing inquiries")}>
                Contact Support
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyPlanTab;
